/**
 * KYC Hook
 * Single instance hook for managing KYC status and preventing duplicate API calls
 */
import logger from "../utils/logger";

import { useState, useEffect, useCallback, useId } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { kycService } from "../services/kycService";
import { supabase } from "../../lib/supabase";

export const useKYC = () => {
  const { user, isLoggedIn } = useAuth();
  // Unique per hook instance so multiple components calling useKYC directly
  // don't collide on the same supabase channel name (which throws
  // "cannot add postgres_changes callbacks ... after subscribe()").
  // Prefer the singleton via KYCContext, but stay robust if a direct caller exists.
  const instanceId = useId();

  // State for KYC status
  const [kycStatus, setKycStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load KYC status
  useEffect(() => {
    if (!isLoggedIn || !user?.id) {
      setKycStatus(null);
      setLoading(false);
      return;
    }

    // Load initial KYC status
    const loadKYCStatus = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await kycService.getKYCStatus();
        setKycStatus(response);
      } catch (err) {
        logger.error("Failed to fetch KYC status:", err);
        setError(err.message);
        // Set default status on error
        setKycStatus({
          status: "pending",
          canRetry: true,
          verificationDetails: null,
        });
      } finally {
        setLoading(false);
      }
    };

    loadKYCStatus();
  }, [isLoggedIn, user?.id]); // Only depend on login status and user ID

  // Real-time subscription for KYC status changes
  useEffect(() => {
    if (!isLoggedIn || !user?.id) {
      return;
    }

    // Subscribe to changes in the players table for this user's KYC status.
    // Channel name includes a per-hook-instance id so concurrent useKYC callers
    // each get a fresh channel (Supabase reuses channels keyed by name).
    const subscription = supabase
      .channel(`kyc-status-${user.id}-${instanceId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "players",
          filter: `id=eq.${user.id}`,
        },
        async (payload) => {
          // Check if KYC-related fields changed
          const oldKycStatus = payload.old?.kyc_status;
          const newKycStatus = payload.new?.kyc_status;

          // CRITICAL FIX: Only process if BOTH values exist AND they're different
          // This prevents false triggers when balance updates don't include kyc_status in the payload
          if (oldKycStatus && newKycStatus && oldKycStatus !== newKycStatus) {
            // Refresh KYC status immediately
            try {
              setLoading(true);
              const response = await kycService.getKYCStatus();
              setKycStatus(response);
              // Force a page refresh for immediate UI update
              // This ensures all components see the new status immediately
              if (newKycStatus === "verified") {
                setTimeout(() => {
                  window.location.reload();
                }, 1000); // Small delay to let the user see any success notifications
              }
            } catch (err) {
              // Silent error handling - don't break the UI
            } finally {
              setLoading(false);
            }
          }
        },
      )
      .subscribe();

    // Cleanup subscription on unmount or user change
    return () => {
      supabase.removeChannel(subscription);
    };
  }, [isLoggedIn, user?.id, instanceId]);

  /**
   * Refresh KYC status manually
   */
  const fetchKYCStatus = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError(null);

      const response = await kycService.getKYCStatus();
      setKycStatus(response);
      return response;
    } catch (err) {
      logger.error("Failed to fetch KYC status:", err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  /**
   * Check if KYC is required for a specific action
   */
  const checkKYCRequired = useCallback(
    async (action) => {
      if (!user?.id) return { kycRequired: false, kycVerified: false };

      try {
        const response = await kycService.checkKYCRequired(action);
        return response;
      } catch (err) {
        logger.error("Failed to check KYC requirement:", err);
        return { kycRequired: false, kycVerified: false };
      }
    },
    [user?.id],
  );

  /**
   * Require KYC for action - returns false if verification needed
   */
  const requireKYCForAction = useCallback(
    async (action) => {
      const requirement = await checkKYCRequired(action);

      if (requirement.kycRequired && !requirement.kycApproved) {
        return false; // Action blocked - caller should navigate to /verify
      }

      return true; // Action allowed
    },
    [checkKYCRequired],
  );

  /**
   * Get KYC status display information
   */
  const getStatusDisplay = useCallback(() => {
    if (!kycStatus) return null;
    return kycService.getStatusDisplay(kycStatus.status);
  }, [kycStatus]);

  // Computed values - simplified approach
  const isVerified =
    kycStatus?.status === "verified" || kycStatus?.isVerified === true;
  const canRetry = kycStatus?.canRetry || !isVerified;
  const rejectionReason = kycStatus?.rejectionReason || null;

  return {
    // Status
    kycStatus,
    isVerified,
    canRetry,
    loading,
    error,
    rejectionReason,

    // Actions
    fetchKYCStatus,
    checkKYCRequired,
    requireKYCForAction,
    getStatusDisplay,
  };
};
